import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import Layout from './Layout';
import {
    Button,
    Card,
    Checkbox,
    Col,
    Collapse,
    DatePicker,
    Drawer,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Progress,
    Row,
    Select,
    AutoComplete,
    Space,
    Statistic,
    Table,
    Tag,
    Timeline,
    Typography,
    message
} from 'antd';
import {
    ArrowDownOutlined,
    ArrowUpOutlined,
    DeleteOutlined,
    DownloadOutlined,
    PlusOutlined,
    ReloadOutlined,
    SettingOutlined
} from '@ant-design/icons';
import './BpClosingFireworks.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

const FIREWORK_PALETTES = [
    ['#fff7d6', '#ffd666', '#fa8c16'],
    ['#ffe6f0', '#ff4d88', '#c41d7f'],
    ['#e6f7ff', '#40a9ff', '#10239e'],
    ['#f6ffed', '#95de64', '#237804'],
    ['#fff1f0', '#ff7875', '#a8071a'],
    ['#f9f0ff', '#b37feb', '#531dab'],
    ['#e6fffb', '#36cfc9', '#006d75']
];


const VAT_RATE = 1.22;
const roundMoney = v => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    if (Number.isNaN(n)) return null;
    return Math.round(n * 100) / 100;
};
const vatFromNet = net => {
    const n = roundMoney(net);
    return n === null ? null : roundMoney(n * VAT_RATE);
};

/** Группы этапов для аккордеона (по code шаблона). */
const STEP_SECTIONS = [
    {
        key: 'export',
        title: '1. Получение выгрузки от заказчика',
        codes: ['request_export', 'export_received', 'draft_report']
    },
    {
        key: 'reconcile',
        title: '2. Сверка объёмов с заказчиком',
        codes: [
            'volume_diffs',
            'penalties_processed',
            'diffs_sent_t2',
            't2_comments',
            'penalty_letter',
            'final_files',
            'report_sent_t2',
            'remarks_1',
            'remarks_fixed_1',
            'remarks_2',
            'remarks_fixed_2',
            'report_agreed',
            'acts_sent',
            'acts_agreed'
        ]
    },
    {
        key: 'report',
        title: '3. Подписание отчёта',
        codes: [
            'report_sent',
            'report_in_edo',
            'report_signed_sks',
            'report_edo_sks',
            'report_signed_t2'
        ]
    },
    {
        key: 'docs',
        title: '4. Контрольно-закрывающие документы',
        codes: [
            'sim_received',
            'penalty_act_signed',
            'docs_uploaded',
            'docs_signed_t2'
        ]
    },
    {
        key: 'payment',
        title: '5. Оплата',
        codes: ['payment_amount', 'payment_eta', 'payment_fact']
    }
];

/** Эти поля редактируются в блоке «Финансы», в дорожной карте скрываем. */
const FINANCE_ONLY_STEP_CODES = new Set([
    'penalty_act_number',
    'penalty_act_amount'
]);

const groupSteps = steps => {
    const list = Array.isArray(steps) ? steps : [];
    const used = new Set();
    const sections = STEP_SECTIONS.map(sec => {
        const items = list.filter(s => sec.codes.includes(s.code));
        items.forEach(s => used.add(s.id));
        return { ...sec, steps: items };
    });
    const rest = list.filter(
        s => !used.has(s.id) && !FINANCE_ONLY_STEP_CODES.has(s.code)
    );
    if (rest.length) {
        const reconcile = sections.find(sec => sec.key === 'reconcile');
        if (reconcile) {
            reconcile.steps = [...reconcile.steps, ...rest];
        } else {
            sections.splice(1, 0, {
                key: 'reconcile',
                title: '2. Сверка объёмов с заказчиком',
                steps: rest
            });
        }
    }
    return sections.filter(sec => sec.steps.length);
};

const sectionProgress = steps => {
    const applicable = (steps || []).filter(s => s.status !== 'na');
    if (!applicable.length) return 100;
    const done = applicable.filter(s =>
        ['done_on_time', 'done_late', 'done'].includes(s.status)
    ).length;
    return Math.round((done / applicable.length) * 100);
};

const PaidFireworks = ({ show, label = 'Оплачено!', onDone }) => {
    const canvasRef = useRef(null);
    const onDoneRef = useRef(onDone);
    onDoneRef.current = onDone;

    useEffect(() => {
        if (!show) return undefined;
        const reduce = window.matchMedia(
            '(prefers-reduced-motion: reduce)'
        ).matches;
        const canvas = canvasRef.current;
        if (!canvas || reduce) {
            const timer = window.setTimeout(() => onDoneRef.current?.(), 1800);
            return () => window.clearTimeout(timer);
        }

        const ctx = canvas.getContext('2d');
        const rockets = [];
        const sparks = [];
        let raf = 0;
        let alive = true;

        const fit = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            canvas.width = Math.floor(window.innerWidth * dpr);
            canvas.height = Math.floor(window.innerHeight * dpr);
            canvas.style.width = '100vw';
            canvas.style.height = '100vh';
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        fit();
        window.addEventListener('resize', fit);

        const pickColor = palette =>
            palette[Math.floor(Math.random() * palette.length)];

        const burst = (x, y, palette, power) => {
            const count = 72 + Math.floor(Math.random() * 48);
            for (let i = 0; i < count; i += 1) {
                const angle = (Math.PI * 2 * i) / count + Math.random() * 0.08;
                const speed = (1.4 + Math.random() * 4.8) * power;
                sparks.push({
                    x,
                    y,
                    vx: Math.cos(angle) * speed,
                    vy: Math.sin(angle) * speed,
                    life: 1,
                    decay: 0.007 + Math.random() * 0.01,
                    size: 1.4 + Math.random() * 2.4,
                    color: pickColor(palette),
                    gravity: 0.028 + Math.random() * 0.02,
                    twinkle: Math.random() > 0.72
                });
            }
            for (let i = 0; i < 18; i += 1) {
                const angle = Math.random() * Math.PI * 2;
                const speed = 0.4 + Math.random() * 1.6;
                sparks.push({
                    x,
                    y,
                    vx: Math.cos(angle) * speed,
                    vy: Math.sin(angle) * speed - 0.4,
                    life: 1,
                    decay: 0.004 + Math.random() * 0.004,
                    size: 2.2 + Math.random() * 1.6,
                    color: '#fffbe6',
                    gravity: 0.012,
                    twinkle: true
                });
            }
        };

        const launch = () => {
            const palette =
                FIREWORK_PALETTES[
                    Math.floor(Math.random() * FIREWORK_PALETTES.length)
                ];
            const w = window.innerWidth;
            const h = window.innerHeight;
            rockets.push({
                x: w * (0.08 + Math.random() * 0.84),
                y: h + 8,
                vx: (Math.random() - 0.5) * 1.4,
                vy: -(7.2 + Math.random() * 4.6),
                color: palette[1],
                palette,
                target: h * (0.08 + Math.random() * 0.42),
                power: 0.85 + Math.random() * 0.45
            });
        };

        let shots = 0;
        const launcher = window.setInterval(() => {
            launch();
            if (Math.random() > 0.35) launch();
            shots += 1;
            if (shots >= 40) window.clearInterval(launcher);
        }, 280);
        launch();
        launch();

        const draw = () => {
            if (!alive) return;
            const w = window.innerWidth;
            const h = window.innerHeight;
            ctx.clearRect(0, 0, w, h);

            for (let i = rockets.length - 1; i >= 0; i -= 1) {
                const rocket = rockets[i];
                rocket.vy += 0.045;
                rocket.x += rocket.vx;
                rocket.y += rocket.vy;
                sparks.push({
                    x: rocket.x,
                    y: rocket.y,
                    vx: (Math.random() - 0.5) * 0.4,
                    vy: 0.6 + Math.random(),
                    life: 0.7,
                    decay: 0.04,
                    size: 1.6,
                    color: rocket.color,
                    gravity: 0.02,
                    twinkle: false
                });
                ctx.beginPath();
                ctx.fillStyle = '#fff';
                ctx.arc(rocket.x, rocket.y, 2.2, 0, Math.PI * 2);
                ctx.fill();
                if (rocket.y <= rocket.target || rocket.vy >= -0.4) {
                    burst(rocket.x, rocket.y, rocket.palette, rocket.power);
                    rockets.splice(i, 1);
                }
            }

            for (let i = sparks.length - 1; i >= 0; i -= 1) {
                const spark = sparks[i];
                spark.vy += spark.gravity;
                spark.vx *= 0.992;
                spark.vy *= 0.992;
                spark.x += spark.vx;
                spark.y += spark.vy;
                spark.life -= spark.decay;
                if (spark.life <= 0) {
                    sparks.splice(i, 1);
                    continue;
                }
                const flicker = spark.twinkle
                    ? 0.45 + Math.abs(Math.sin(spark.life * 28)) * 0.55
                    : 1;
                ctx.globalAlpha = Math.max(0, spark.life) * flicker;
                ctx.fillStyle = spark.color;
                ctx.shadowColor = spark.color;
                ctx.shadowBlur = 12;
                ctx.beginPath();
                ctx.arc(spark.x, spark.y, spark.size, 0, Math.PI * 2);
                ctx.fill();
            }
            ctx.globalAlpha = 1;
            ctx.shadowBlur = 0;
            raf = window.requestAnimationFrame(draw);
        };
        raf = window.requestAnimationFrame(draw);

        const done = window.setTimeout(() => onDoneRef.current?.(), 15000);
        return () => {
            alive = false;
            window.clearInterval(launcher);
            window.clearTimeout(done);
            window.cancelAnimationFrame(raf);
            window.removeEventListener('resize', fit);
        };
    }, [show]);

    if (!show) return null;

    return createPortal(
        <div className="bp-fireworks-overlay" aria-hidden="true">
            <canvas ref={canvasRef} className="bp-fireworks-canvas" />
            <div className="bp-fireworks-label">{label}</div>
        </div>,
        document.body
    );
};

const money = n =>
    n === null || n === undefined || n === ''
        ? '—'
        : Number(n).toLocaleString('ru-RU', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
          });

const statusTag = status => {
    const map = {
        done: ['Выполнено', 'green'],
        done_on_time: ['Выполнено в срок', 'green'],
        done_late: ['Выполнено с просрочкой', 'orange'],
        na: ['Не применимо', 'default'],
        overdue: ['Просрочено', 'red'],
        due_soon: ['Срок приближается', 'gold'],
        waiting: ['В работе', 'blue'],
        waiting_t2: ['Ожидание Т2', 'purple'],
        empty: ['Не начато', 'orange']
    };
    const [text, color] = map[status] || [status, 'default'];
    return <Tag color={color}>{text}</Tag>;
};

const recordStatusTag = (status, label) => {
    const map = {
        new: ['Новое', 'default'],
        reconcile_customer: ['Сверка на стороне Заказчика', 'purple'],
        reconcile_sks: ['Сверка на стороне СКС', 'blue'],
        sign_report_edo: ['Подписание отчета в ЭДО', 'geekblue'],
        sign_acts_edo: ['Подписание актов в ЭДО', 'cyan'],
        in_payment: ['В оплате', 'gold'],
        paid: ['Оплачено', 'green'],
        archived: ['Архив', 'default'],
        // старые коды на всякий случай
        awaiting_payment: ['В оплате', 'gold'],
        in_progress: ['Сверка на стороне СКС', 'blue']
    };
    const [text, color] = map[status] || [label || status || '—', 'default'];
    return <Tag color={color}>{text}</Tag>;
};

const suggestTemplateCode = region => {
    const r = String(region || '').toLowerCase();
    if (r.includes('москв')) return 'msk';
    if (
        r.includes('ижевск') ||
        r.includes('чебоксар') ||
        r.includes('йошкар')
    ) {
        return 'volga_short';
    }
    return 'regions_activmap';
};

const BpClosingPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [regions, setRegions] = useState([]);
    const [orders, setOrders] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const [onlyOverdue, setOnlyOverdue] = useState(
        searchParams.get('overdue') === '1'
    );
    const [regionFilter, setRegionFilter] = useState(
        searchParams.get('region') || null
    );
    const [periodFilter, setPeriodFilter] = useState('');
    const [opexFilter, setOpexFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    const [searchQ, setSearchQ] = useState('');

    const [createOpen, setCreateOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form] = Form.useForm();

    const [detail, setDetail] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [savingStep, setSavingStep] = useState(null);
    const [paymentForm] = Form.useForm();

    const [tplOpen, setTplOpen] = useState(false);
    const [tplId, setTplId] = useState(null);
    const [newStepTitle, setNewStepTitle] = useState('');
    const [newStepSla, setNewStepSla] = useState(1);

    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkRegions, setBulkRegions] = useState([]);
    const [bulkPeriods, setBulkPeriods] = useState([]);
    const [bulkReason, setBulkReason] = useState('');
    const [firework, setFirework] = useState(null);

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`
    });

    const listParams = useCallback(() => {
        const params = {};
        if (onlyOverdue) params.overdue = 1;
        if (regionFilter) params.region = regionFilter;
        if (periodFilter) params.period = periodFilter;
        if (opexFilter) params.opexCapex = opexFilter;
        if (statusFilter) params.status = statusFilter;
        if (searchQ) params.q = searchQ;
        return params;
    }, [
        onlyOverdue,
        regionFilter,
        periodFilter,
        opexFilter,
        statusFilter,
        searchQ
    ]);

    const applyMeta = payload => {
        setTemplates(payload.templates || []);
        setRegions(payload.regions || []);
    };

    const loadMeta = useCallback(async () => {
        const [tplRes, usersRes] = await Promise.all([
            axios.get('/api/bp-closing/templates', { headers: authHeaders() }),
            axios.get('/api/bp-closing/users', { headers: authHeaders() })
        ]);
        applyMeta(tplRes.data.data || tplRes.data || {});
        setUsers(usersRes.data.data || usersRes.data || []);
    }, []);

    const loadRows = useCallback(async () => {
        setLoading(true);
        try {
            const params = listParams();
            const [listRes, sumRes] = await Promise.all([
                axios.get('/api/bp-closing', {
                    params,
                    headers: authHeaders()
                }),
                axios.get('/api/bp-closing/summary', {
                    params,
                    headers: authHeaders()
                })
            ]);
            setRows(listRes.data.data || listRes.data || []);
            setSummary(sumRes.data.data || sumRes.data || null);
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    error.message ||
                    'Не удалось загрузить закрытия'
            );
        } finally {
            setLoading(false);
        }
    }, [listParams]);

    const loadOrders = useCallback(async () => {
        const { data } = await axios.get('/api/bp-closing/orders', {
            headers: authHeaders()
        });
        setOrders(data.data || data || []);
    }, []);

    useEffect(() => {
        loadMeta();
    }, [loadMeta]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    useEffect(() => {
        const next = {};
        if (onlyOverdue) next.overdue = '1';
        if (regionFilter) next.region = regionFilter;
        setSearchParams(next, { replace: true });
    }, [onlyOverdue, regionFilter, setSearchParams]);

    const regionOptions = useMemo(
        () =>
            [...new Set([...regions, ...rows.map(r => r.region)].filter(Boolean))]
                .sort((a, b) => a.localeCompare(b, 'ru'))
                .map(r => ({ value: r, label: r })),
        [regions, rows]
    );

    const periodOptions = useMemo(
        () =>
            [...new Set(rows.map(r => r.period).filter(Boolean))]
                .sort((a, b) => a.localeCompare(b, 'ru'))
                .map(p => ({ value: p, label: p })),
        [rows]
    );

    const userOptions = useMemo(
        () => users.map(u => ({ value: u.id, label: u.name })),
        [users]
    );

    const openCreate = async () => {
        form.resetFields();
        form.setFieldsValue({ opexCapex: 'Opex' });
        await loadOrders();
        setCreateOpen(true);
    };

    const onRegionChange = region => {
        const code = suggestTemplateCode(region);
        const tpl = templates.find(t => t.code === code);
        if (tpl) form.setFieldsValue({ templateId: tpl.id });
    };

    const submitCreate = async values => {
        setCreating(true);
        try {
            await axios.post(
                '/api/bp-closing',
                {
                    registryId: values.registryId || null,
                    templateId: values.templateId,
                    region: values.region,
                    period: values.period,
                    opexCapex: values.opexCapex,
                    closeDate: values.closeDate?.format?.('YYYY-MM-DD'),
                    amountNoVat: values.amountNoVat,
                    amountVat: values.amountVat
                },
                { headers: authHeaders() }
            );
            message.success('Закрытие создано');
            setCreateOpen(false);
            loadRows();
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    error.message ||
                    'Не удалось создать'
            );
        } finally {
            setCreating(false);
        }
    };

    const openDetail = async record => {
        try {
            const { data } = await axios.get(`/api/bp-closing/${record.id}`, {
                headers: authHeaders()
            });
            setDetail(data.data || data);
            paymentForm.resetFields();
            setDetailOpen(true);
        } catch (error) {
            message.error(
                error.response?.data?.message || 'Не удалось открыть карточку'
            );
        }
    };

    const saveStep = async (step, patch) => {
        if (!detail) return;
        setSavingStep(step.id);
        try {
            const { data } = await axios.patch(
                `/api/bp-closing/${detail.id}/steps/${step.id}`,
                patch,
                { headers: authHeaders() }
            );
            const next = data.data || data;
            const becamePaid =
                (next.is_paid || next.record_status === 'paid') &&
                !(detail.is_paid || detail.record_status === 'paid');
            setDetail(next);
            setRows(prev => prev.map(r => (r.id === next.id ? next : r)));
            if (becamePaid) celebratePaid('Оплачено!');
            loadRows();
        } catch (error) {
            message.error(
                error.response?.data?.message || 'Не удалось сохранить шаг'
            );
        } finally {
            setSavingStep(null);
        }
    };

    const celebratePaid = (label = 'Оплачено!') => {
        setFirework({ id: Date.now(), label });
    };

    const markPaid = async () => {
        if (!detail) return;
        try {
            const { data } = await axios.post(
                `/api/bp-closing/${detail.id}/mark-paid`,
                {},
                { headers: authHeaders() }
            );
            const next = data.data || data;
            setDetail(next);
            message.success('Отмечено как оплачено');
            celebratePaid('Оплачено!');
            loadRows();
        } catch (error) {
            message.error(
                error.response?.data?.message || 'Не удалось отметить оплату'
            );
        }
    };

    const copyFromSibling = async () => {
        if (!detail?.sibling?.id) {
            message.warning(
                `Нет парной записи ${
                    detail?.opex_capex === 'Capex' ? 'Opex' : 'Capex'
                } за этот период`
            );
            return;
        }
        try {
            const { data } = await axios.post(
                `/api/bp-closing/${detail.id}/copy-from-sibling`,
                {},
                { headers: authHeaders() }
            );
            const next = data.data || data;
            const result = next.copy_result || {};
            const { copy_result: _cr, ...closing } = next;
            setDetail(closing);
            setRows(prev =>
                prev.map(r => (r.id === closing.id ? { ...r, ...closing } : r))
            );
            message.success(
                `Скопировано этапов: ${result.copied || 0}` +
                    (result.skipped_filled
                        ? ` (пропущено уже заполненных: ${result.skipped_filled})`
                        : '')
            );
            loadRows();
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Не удалось скопировать из парной записи'
            );
        }
    };

    const unmarkPaid = async () => {
        if (!detail) return;
        try {
            const { data } = await axios.post(
                `/api/bp-closing/${detail.id}/unmark-paid`,
                {},
                { headers: authHeaders() }
            );
            const next = data.data || data;
            setDetail(next);
            setRows(prev => prev.map(r => (r.id === next.id ? next : r)));
            message.success('Оплата снята');
            loadRows();
        } catch (error) {
            message.error(
                error.response?.data?.message || 'Не удалось снять оплату'
            );
        }
    };

    const deletePayment = async paymentId => {
        if (!detail) return;
        try {
            const { data } = await axios.delete(
                `/api/bp-closing/${detail.id}/payments/${paymentId}`,
                { headers: authHeaders() }
            );
            const next = data.data || data;
            setDetail(next);
            setRows(prev => prev.map(r => (r.id === next.id ? next : r)));
            message.success('Оплата удалена');
            loadRows();
        } catch (error) {
            message.error(
                error.response?.data?.message || 'Не удалось удалить оплату'
            );
        }
    };

    const saveAmounts = async patch => {
        if (!detail || detail.archived_at) return;
        try {
            const { data } = await axios.patch(
                `/api/bp-closing/${detail.id}`,
                patch,
                { headers: authHeaders() }
            );
            const next = data.data || data;
            setDetail(next);
            setRows(prev => prev.map(r => (r.id === next.id ? next : r)));
            message.success('Суммы сохранены');
            loadRows();
        } catch (error) {
            message.error(
                error.response?.data?.message || 'Не удалось сохранить суммы'
            );
        }
    };

    const archiveClosing = () => {
        if (!detail) return;
        let reason = '';
        Modal.confirm({
            title: 'Архивировать запись?',
            content: (
                <Input.TextArea
                    rows={3}
                    placeholder="Причина архивирования"
                    onChange={e => {
                        reason = e.target.value;
                    }}
                />
            ),
            okText: 'В архив',
            onOk: async () => {
                if (!String(reason || '').trim()) {
                    message.error('Укажите причину');
                    throw new Error('reason');
                }
                await axios.post(
                    `/api/bp-closing/${detail.id}/archive`,
                    { reason },
                    { headers: authHeaders() }
                );
                message.success('Запись архивирована');
                setDetailOpen(false);
                setDetail(null);
                loadRows();
            }
        });
    };

    const submitBulkArchive = async () => {
        if (!String(bulkReason || '').trim()) {
            message.error('Укажите причину');
            return;
        }
        try {
            const body = { reason: bulkReason };
            if (selectedRowKeys.length) {
                body.ids = selectedRowKeys;
            } else {
                body.regions = bulkRegions;
                body.periods = bulkPeriods;
            }
            const { data } = await axios.post(
                '/api/bp-closing/archive-bulk',
                body,
                { headers: authHeaders() }
            );
            message.success(
                `Архивировано: ${data.data?.archived ?? data.archived ?? 0}`
            );
            setBulkOpen(false);
            setSelectedRowKeys([]);
            setBulkRegions([]);
            setBulkPeriods([]);
            setBulkReason('');
            loadRows();
        } catch (error) {
            message.error(
                error.response?.data?.message || 'Не удалось архивировать'
            );
        }
    };

    const addPayment = async values => {
        if (!detail) return;
        try {
            const { data } = await axios.post(
                `/api/bp-closing/${detail.id}/payments`,
                {
                    amount: values.amount,
                    paidAt: values.paidAt?.format?.('YYYY-MM-DD'),
                    comment: values.comment
                },
                { headers: authHeaders() }
            );
            const next = data.data || data;
            setDetail(next);
            paymentForm.resetFields();
            message.success('Оплата добавлена');
            const fullyPaid =
                next.is_paid || next.record_status === 'paid';
            celebratePaid(fullyPaid ? 'Оплачено!' : 'Оплата!');
            loadRows();
        } catch (error) {
            message.error(
                error.response?.data?.message || 'Не удалось добавить оплату'
            );
        }
    };

    const exportXlsx = async () => {
        try {
            const res = await axios.get('/api/bp-closing/export', {
                params: listParams(),
                headers: authHeaders(),
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'kontrol-zakrytiya.xlsx';
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            message.error('Не удалось выгрузить Excel');
        }
    };

    const refreshTemplates = async data => {
        const payload = data?.data || data || {};
        if (payload.templates) applyMeta(payload);
        else await loadMeta();
    };

    const moveTplStep = async (stepId, direction) => {
        const { data } = await axios.post(
            `/api/bp-closing/template-steps/${stepId}/move`,
            { direction },
            { headers: authHeaders() }
        );
        await refreshTemplates(data);
    };

    const deleteTplStep = async stepId => {
        const { data } = await axios.delete(
            `/api/bp-closing/template-steps/${stepId}`,
            { headers: authHeaders() }
        );
        await refreshTemplates(data);
        message.success('Этап удалён');
    };

    const saveTplStep = async (stepId, patch) => {
        const { data } = await axios.patch(
            `/api/bp-closing/template-steps/${stepId}`,
            patch,
            { headers: authHeaders() }
        );
        await refreshTemplates(data);
    };

    const addTplStep = async () => {
        if (!tplId || !newStepTitle.trim()) {
            message.error('Укажите название этапа');
            return;
        }
        const { data } = await axios.post(
            `/api/bp-closing/templates/${tplId}/steps`,
            { title: newStepTitle, slaDays: newStepSla, kind: 'date' },
            { headers: authHeaders() }
        );
        setNewStepTitle('');
        setNewStepSla(1);
        await refreshTemplates(data);
        message.success('Этап добавлен');
    };

    const activeTpl = templates.find(t => t.id === tplId);

    const columns = useMemo(
        () => [
            {
                title: 'Регион',
                dataIndex: 'region',
                width: 150,
                fixed: 'left',
                sorter: (a, b) =>
                    String(a.region || '').localeCompare(
                        String(b.region || ''),
                        'ru'
                    )
            },
            {
                title: 'Период',
                dataIndex: 'period',
                width: 120
            },
            {
                title: 'Тип',
                dataIndex: 'opex_capex',
                width: 80
            },
            {
                title: 'Дата закрытия',
                dataIndex: 'close_date',
                width: 110
            },
            {
                title: 'Без НДС',
                dataIndex: 'amount_no_vat',
                width: 120,
                align: 'right',
                render: money
            },
            {
                title: 'С НДС',
                dataIndex: 'amount_vat',
                width: 120,
                align: 'right',
                render: money
            },
            {
                title: 'Акт недостач',
                dataIndex: 'penalty_amount',
                width: 120,
                align: 'right',
                render: money
            },
            {
                title: 'К оплате',
                dataIndex: 'amount_to_pay',
                width: 130,
                align: 'right',
                render: v => (
                    <Text strong style={{ color: '#1677ff' }}>
                        {money(v)}
                    </Text>
                )
            },
            {
                title: 'Остаток',
                dataIndex: 'balance',
                width: 110,
                align: 'right',
                render: money
            },
            {
                title: 'Прогресс',
                dataIndex: 'progress_pct',
                width: 120,
                render: (v, row) => (
                    <Progress
                        percent={Number(v) || 0}
                        size="small"
                        status={row.has_overdue ? 'exception' : undefined}
                    />
                )
            },
            {
                title: 'Текущий этап',
                dataIndex: 'current_step',
                width: 210,
                render: v =>
                    v ? (
                        <Space direction="vertical" size={0}>
                            <span>{v.title}</span>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {v.responsible || ''}
                                {v.due_at ? ` · до ${v.due_at}` : ''}
                            </Text>
                        </Space>
                    ) : (
                        <Tag color="green">завершено</Tag>
                    )
            },
            {
                title: 'Статус',
                dataIndex: 'record_status',
                width: 210,
                render: (v, row) =>
                    recordStatusTag(v, row.record_status_label)
            },
            {
                title: '',
                width: 90,
                fixed: 'right',
                render: (_, row) => (
                    <Button type="link" onClick={() => openDetail(row)}>
                        Открыть
                    </Button>
                )
            }
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    return (
        <Layout>
            <PaidFireworks
                key={firework?.id || 'idle'}
                show={Boolean(firework)}
                label={firework?.label}
                onDone={() => setFirework(null)}
            />
            <div style={{ padding: 20, maxWidth: 1720, margin: '0 auto' }}>
                <Title level={3} style={{ marginBottom: 4 }}>
                    Контроль закрытия работ
                </Title>
                <Text type="secondary">
                    Реестр по БП: этапы, SLA, просрочки и оплаты.
                </Text>

                {summary ? (
                    <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
                        <Col xs={12} sm={8} md={4}>
                            <Card size="small">
                                <Statistic
                                    title="В работе"
                                    value={summary.amount_in_work || 0}
                                    precision={0}
                                    groupSeparator=" "
                                    valueStyle={{ color: '#1677ff' }}
                                />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    до «Акт и ВСО подписаны Т2» ·{' '}
                                    {summary.in_work_count || 0} зап.
                                </Text>
                            </Card>
                        </Col>
                        <Col xs={12} sm={8} md={4}>
                            <Card size="small">
                                <Statistic
                                    title="Ожидает оплату"
                                    value={summary.amount_awaiting_payment || 0}
                                    precision={0}
                                    groupSeparator=" "
                                    valueStyle={{ color: '#08979c' }}
                                />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    после подписи Т2 ·{' '}
                                    {summary.awaiting_payment_count || 0} зап.
                                </Text>
                            </Card>
                        </Col>
                        <Col xs={12} sm={8} md={4}>
                            <Card size="small">
                                <Statistic
                                    title="Просрочено"
                                    value={summary.overdue}
                                    valueStyle={{
                                        color:
                                            summary.overdue > 0
                                                ? '#cf1322'
                                                : undefined
                                    }}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} sm={8} md={4}>
                            <Card size="small">
                                <Statistic
                                    title="Срок скоро"
                                    value={summary.due_soon}
                                    valueStyle={{ color: '#d48806' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} sm={8} md={4}>
                            <Card size="small">
                                <Statistic
                                    title="Оплачено"
                                    value={summary.amount_paid || 0}
                                    precision={0}
                                    groupSeparator=" "
                                />
                            </Card>
                        </Col>
                        <Col xs={12} sm={8} md={4}>
                            <Card size="small">
                                <Statistic
                                    title="Активных"
                                    value={summary.total}
                                />
                            </Card>
                        </Col>
                    </Row>
                ) : null}

                {summary?.penalty_stats?.length ? (
                    <Card
                        size="small"
                        title="Штрафы по регионам, %"
                        style={{ marginTop: 12 }}
                    >
                        <Table
                            size="small"
                            pagination={false}
                            rowKey="region"
                            dataSource={summary.penalty_stats.slice(0, 12)}
                            columns={[
                                { title: 'Регион', dataIndex: 'region' },
                                {
                                    title: 'Сумма с НДС',
                                    dataIndex: 'amount_vat',
                                    align: 'right',
                                    render: money
                                },
                                {
                                    title: 'Штрафы',
                                    dataIndex: 'penalty_amount',
                                    align: 'right',
                                    render: money
                                },
                                {
                                    title: '% штрафа',
                                    dataIndex: 'penalty_pct',
                                    align: 'right',
                                    render: v => `${v}%`
                                }
                            ]}
                        />
                    </Card>
                ) : null}

                <Card style={{ marginTop: 16 }}>
                    <Space wrap style={{ marginBottom: 12 }}>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openCreate}
                        >
                            Новое закрытие
                        </Button>
                        <Button icon={<ReloadOutlined />} onClick={loadRows}>
                            Обновить
                        </Button>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={exportXlsx}
                        >
                            Excel
                        </Button>
                        <Button
                            icon={<SettingOutlined />}
                            onClick={() => {
                                setTplId(templates[0]?.id || null);
                                setTplOpen(true);
                            }}
                        >
                            Этапы БП
                        </Button>
                        <Button
                            danger
                            onClick={() => {
                                setBulkRegions(
                                    regionFilter ? [regionFilter] : []
                                );
                                setBulkOpen(true);
                            }}
                        >
                            Архив
                            {selectedRowKeys.length
                                ? ` (${selectedRowKeys.length})`
                                : ''}
                        </Button>
                        <Checkbox
                            checked={onlyOverdue}
                            onChange={e => setOnlyOverdue(e.target.checked)}
                        >
                            Только просроченные
                        </Checkbox>
                        <Select
                            allowClear
                            showSearch
                            placeholder="Регион"
                            style={{ minWidth: 200 }}
                            value={regionFilter}
                            onChange={setRegionFilter}
                            options={regionOptions}
                        />
                        <Input
                            allowClear
                            placeholder="Период"
                            style={{ width: 140 }}
                            value={periodFilter}
                            onChange={e => setPeriodFilter(e.target.value)}
                        />
                        <Select
                            allowClear
                            placeholder="Opex/Capex"
                            style={{ width: 130 }}
                            value={opexFilter}
                            onChange={setOpexFilter}
                            options={[
                                { value: 'Opex', label: 'Opex' },
                                { value: 'Capex', label: 'Capex' }
                            ]}
                        />
                        <Select
                            allowClear
                            placeholder="Статус"
                            style={{ width: 260 }}
                            value={statusFilter}
                            onChange={setStatusFilter}
                            options={[
                                { value: 'new', label: 'Новое' },
                                {
                                    value: 'reconcile_sks',
                                    label: 'Сверка на стороне СКС'
                                },
                                {
                                    value: 'reconcile_customer',
                                    label: 'Сверка на стороне Заказчика'
                                },
                                {
                                    value: 'sign_report_edo',
                                    label: 'Подписание отчета в ЭДО'
                                },
                                {
                                    value: 'sign_acts_edo',
                                    label: 'Подписание актов в ЭДО'
                                },
                                { value: 'in_payment', label: 'В оплате' },
                                { value: 'paid', label: 'Оплачено' }
                            ]}
                        />
                        <Input.Search
                            allowClear
                            placeholder="Поиск"
                            style={{ width: 200 }}
                            onSearch={setSearchQ}
                            onChange={e => {
                                if (!e.target.value) setSearchQ('');
                            }}
                        />
                    </Space>
                    <Table
                        rowKey="id"
                        size="small"
                        loading={loading}
                        columns={columns}
                        dataSource={rows}
                        rowSelection={{
                            selectedRowKeys,
                            onChange: setSelectedRowKeys
                        }}
                        pagination={{ pageSize: 25, showSizeChanger: true }}
                        scroll={{ x: 1500 }}
                        onRow={record => ({
                            style: record.has_overdue
                                ? { background: '#fff1f0' }
                                : record.has_due_soon
                                  ? { background: '#fffbe6' }
                                  : undefined
                        })}
                    />
                </Card>
            </div>

            <Modal
                open={createOpen}
                title="Новое закрытие"
                onCancel={() => setCreateOpen(false)}
                onOk={() => form.submit()}
                confirmLoading={creating}
                okText="Создать"
                width={640}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={submitCreate}>
                    <Form.Item
                        name="registryId"
                        label="Заказ покупателя (необязательно)"
                    >
                        <Select
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            options={orders.map(o => ({
                                value: o.id,
                                label: `${o.number} · ${o.counterparty_name || ''}`.trim()
                            }))}
                        />
                    </Form.Item>
                    <Form.Item
                        name="region"
                        label="Регион"
                        rules={[{ required: true }]}
                    >
                        <AutoComplete
                            options={regionOptions}
                            onChange={onRegionChange}
                        />
                    </Form.Item>
                    <Form.Item
                        name="templateId"
                        label="Шаблон процесса"
                        rules={[{ required: true }]}
                    >
                        <Select
                            options={templates.map(t => ({
                                value: t.id,
                                label: t.name
                            }))}
                        />
                    </Form.Item>
                    <Form.Item
                        name="period"
                        label="Период"
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="opexCapex"
                        label="Opex / Capex"
                        rules={[{ required: true }]}
                    >
                        <Select
                            options={[
                                { value: 'Opex', label: 'Opex' },
                                { value: 'Capex', label: 'Capex' }
                            ]}
                        />
                    </Form.Item>
                    <Form.Item
                        name="closeDate"
                        label="Дата закрытия периода"
                        rules={[{ required: true }]}
                    >
                        <DatePicker
                            style={{ width: '100%' }}
                            format="DD.MM.YYYY"
                        />
                    </Form.Item>
                    <Space>
                        <Form.Item name="amountNoVat" label="Сумма без НДС">
                            <InputNumber style={{ width: 180 }} />
                        </Form.Item>
                        <Form.Item name="amountVat" label="Сумма с НДС">
                            <InputNumber style={{ width: 180 }} />
                        </Form.Item>
                    </Space>
                </Form>
            </Modal>

            <Modal
                open={bulkOpen}
                title="Массовое архивирование"
                onCancel={() => setBulkOpen(false)}
                onOk={submitBulkArchive}
                okText="В архив"
                okButtonProps={{ danger: true }}
            >
                <Text type="secondary">
                    {selectedRowKeys.length
                        ? `Выбрано записей: ${selectedRowKeys.length}`
                        : 'Или укажите регионы и/или периоды'}
                </Text>
                {!selectedRowKeys.length ? (
                    <>
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder="Регионы"
                            style={{ width: '100%', marginTop: 12 }}
                            value={bulkRegions}
                            onChange={setBulkRegions}
                            options={regionOptions}
                        />
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder="Периоды"
                            style={{ width: '100%', marginTop: 12 }}
                            value={bulkPeriods}
                            onChange={setBulkPeriods}
                            options={periodOptions}
                        />
                    </>
                ) : null}
                <TextArea
                    rows={3}
                    style={{ marginTop: 12 }}
                    placeholder="Причина архивирования"
                    value={bulkReason}
                    onChange={e => setBulkReason(e.target.value)}
                />
            </Modal>

            <Modal
                open={tplOpen}
                title="Дорожная карта этапов БП"
                onCancel={() => setTplOpen(false)}
                footer={null}
                width={960}
            >
                <Select
                    style={{ width: '100%', marginBottom: 12 }}
                    value={tplId}
                    onChange={setTplId}
                    options={templates.map(t => ({
                        value: t.id,
                        label: t.name
                    }))}
                />
                {(activeTpl?.regions || []).length > 1 ? (
                    <Text
                        type="secondary"
                        style={{ display: 'block', marginBottom: 12 }}
                    >
                        Для шаблона несколько регионов — назначьте ответственного
                        отдельно по каждому. «По умолчанию» используется, если для
                        региона не задан свой.
                    </Text>
                ) : null}
                {(activeTpl?.steps || [])
                    .slice()
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map(step => (
                        <Card
                            key={step.id}
                            size="small"
                            style={{ marginBottom: 8 }}
                            title={`${step.sort_order}. ${step.title}`}
                            extra={
                                <Space>
                                    <Button
                                        size="small"
                                        icon={<ArrowUpOutlined />}
                                        onClick={() =>
                                            moveTplStep(step.id, 'up')
                                        }
                                    />
                                    <Button
                                        size="small"
                                        icon={<ArrowDownOutlined />}
                                        onClick={() =>
                                            moveTplStep(step.id, 'down')
                                        }
                                    />
                                    <Popconfirm
                                        title="Удалить этап?"
                                        onConfirm={() =>
                                            deleteTplStep(step.id)
                                        }
                                    >
                                        <Button
                                            size="small"
                                            danger
                                            icon={<DeleteOutlined />}
                                        />
                                    </Popconfirm>
                                </Space>
                            }
                        >
                            <Space wrap style={{ marginBottom: 8 }}>
                                <span>SLA (дн.):</span>
                                <InputNumber
                                    size="small"
                                    value={step.sla_days}
                                    onBlur={e =>
                                        saveTplStep(step.id, {
                                            slaDays:
                                                e.target.value === ''
                                                    ? null
                                                    : Number(e.target.value)
                                        })
                                    }
                                    onPressEnter={e =>
                                        saveTplStep(step.id, {
                                            slaDays:
                                                e.target.value === ''
                                                    ? null
                                                    : Number(e.target.value)
                                        })
                                    }
                                />
                                <span>По умолчанию:</span>
                                <Select
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    size="small"
                                    style={{ minWidth: 220 }}
                                    value={step.responsible_user_id || undefined}
                                    options={userOptions}
                                    placeholder={step.responsible || 'Выберите'}
                                    onChange={v =>
                                        saveTplStep(step.id, {
                                            responsibleUserId: v || null
                                        })
                                    }
                                />
                            </Space>
                            {(activeTpl?.regions || []).length > 1 ? (
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns:
                                            'repeat(auto-fill, minmax(260px, 1fr))',
                                        gap: 8
                                    }}
                                >
                                    {(activeTpl.regions || []).map(region => {
                                        const byRegion =
                                            step.responsible_by_region || {};
                                        const current = byRegion[region];
                                        return (
                                            <Space
                                                key={`${step.id}-${region}`}
                                                direction="vertical"
                                                size={2}
                                                style={{ width: '100%' }}
                                            >
                                                <Text
                                                    type="secondary"
                                                    style={{ fontSize: 12 }}
                                                >
                                                    {region}
                                                </Text>
                                                <Select
                                                    allowClear
                                                    showSearch
                                                    optionFilterProp="label"
                                                    size="small"
                                                    style={{ width: '100%' }}
                                                    value={
                                                        current?.user_id ||
                                                        undefined
                                                    }
                                                    options={userOptions}
                                                    placeholder={
                                                        current?.name ||
                                                        step.responsible ||
                                                        'Как по умолчанию'
                                                    }
                                                    onChange={v =>
                                                        saveTplStep(step.id, {
                                                            responsibleByRegion:
                                                                {
                                                                    [region]:
                                                                        v
                                                                            ? {
                                                                                  userId: v
                                                                              }
                                                                            : null
                                                                }
                                                        })
                                                    }
                                                />
                                            </Space>
                                        );
                                    })}
                                </div>
                            ) : null}
                        </Card>
                    ))}
                <Space style={{ marginTop: 8 }}>
                    <Input
                        placeholder="Новый этап"
                        value={newStepTitle}
                        onChange={e => setNewStepTitle(e.target.value)}
                    />
                    <InputNumber
                        placeholder="SLA"
                        value={newStepSla}
                        onChange={setNewStepSla}
                    />
                    <Button type="primary" onClick={addTplStep}>
                        Добавить
                    </Button>
                </Space>
            </Modal>

            <Drawer
                open={detailOpen}
                width="95%"
                styles={{ body: { background: '#f5f7fb' } }}
                title={
                    detail
                        ? `${detail.region} · ${detail.period} · ${detail.opex_capex}`
                        : 'Закрытие'
                }
                onClose={() => {
                    setDetailOpen(false);
                    setDetail(null);
                }}
                extra={
                    detail && !detail.archived_at ? (
                        <Space wrap>
                            {detail.sibling?.id ? (
                                <Popconfirm
                                    title={`Скопировать из ${detail.sibling.opex_capex}?`}
                                    description="Заполнит пустые этапы сверки из парной записи. Подписание, ЭДО и оплату не трогаем."
                                    okText="Скопировать"
                                    cancelText="Отмена"
                                    onConfirm={copyFromSibling}
                                >
                                    <Button>
                                        Из {detail.sibling.opex_capex}
                                    </Button>
                                </Popconfirm>
                            ) : null}
                            {!detail.is_paid ? (
                                <Button type="primary" onClick={markPaid}>
                                    Оплачено
                                </Button>
                            ) : (
                                <Tag color="green">Оплачено</Tag>
                            )}
                            {detail.is_paid ||
                            (detail.payments || []).length > 0 ? (
                                <Popconfirm
                                    title="Снять оплату?"
                                    description="Удалятся все платежи и сбросятся этапы оплаты."
                                    okText="Снять"
                                    cancelText="Отмена"
                                    onConfirm={unmarkPaid}
                                >
                                    <Button danger>Снять оплату</Button>
                                </Popconfirm>
                            ) : null}
                            <Button onClick={archiveClosing}>В архив</Button>
                        </Space>
                    ) : null
                }
            >
                {detail ? (
                    <Space
                        direction="vertical"
                        size={16}
                        style={{ width: '100%' }}
                    >
                        <Card size="small">
                            <Space wrap>
                                {recordStatusTag(
                                    detail.record_status,
                                    detail.record_status_label
                                )}
                                <Tag>{detail.template_name}</Tag>
                                <Text type="secondary">
                                    Закрытие: {detail.close_date}
                                </Text>
                            </Space>
                            <Row gutter={12} style={{ marginTop: 12 }}>
                                <Col span={8}>
                                    <Statistic
                                        title="Прогресс"
                                        value={detail.progress_pct}
                                        suffix="%"
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title="Документы"
                                        value={detail.doc_progress}
                                        suffix="%"
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title="Финансы"
                                        value={detail.finance_progress}
                                        suffix="%"
                                    />
                                </Col>
                            </Row>
                        </Card>

                        <Card size="small" title="Финансы">
                            <Row gutter={[12, 12]}>
                                <Col xs={24} sm={8}>
                                    <Text type="secondary">Без НДС</Text>
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        value={detail.amount_no_vat}
                                        disabled={!!detail.archived_at}
                                        min={0}
                                        step={0.01}
                                        onChange={v =>
                                            setDetail(prev => {
                                                if (!prev) return prev;
                                                const amountVat = vatFromNet(v);
                                                const penalty =
                                                    prev.penalty_amount || 0;
                                                const amountToPay =
                                                    amountVat === null
                                                        ? null
                                                        : roundMoney(
                                                              Math.max(
                                                                  0,
                                                                  amountVat -
                                                                      penalty
                                                              )
                                                          );
                                                return {
                                                    ...prev,
                                                    amount_no_vat: v,
                                                    amount_vat: amountVat,
                                                    amount_to_pay: amountToPay,
                                                    balance:
                                                        amountToPay === null
                                                            ? null
                                                            : roundMoney(
                                                                  amountToPay -
                                                                      (prev.paid_amount ||
                                                                          0)
                                                              )
                                                };
                                            })
                                        }
                                    />
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Text type="secondary">
                                        С НДС (авто ×1.22, можно поправить)
                                    </Text>
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        value={detail.amount_vat}
                                        disabled={!!detail.archived_at}
                                        min={0}
                                        step={0.01}
                                        onChange={v =>
                                            setDetail(prev => {
                                                if (!prev) return prev;
                                                const amountVat = roundMoney(v);
                                                const penalty =
                                                    prev.penalty_amount || 0;
                                                const amountToPay =
                                                    amountVat === null
                                                        ? null
                                                        : roundMoney(
                                                              Math.max(
                                                                  0,
                                                                  amountVat -
                                                                      penalty
                                                              )
                                                          );
                                                return {
                                                    ...prev,
                                                    amount_vat: amountVat,
                                                    amount_to_pay: amountToPay,
                                                    balance:
                                                        amountToPay === null
                                                            ? null
                                                            : roundMoney(
                                                                  amountToPay -
                                                                      (prev.paid_amount ||
                                                                          0)
                                                              )
                                                };
                                            })
                                        }
                                    />
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Text type="secondary">К оплате</Text>
                                    <div>
                                        <Text strong style={{ fontSize: 18 }}>
                                            {money(detail.amount_to_pay)}
                                        </Text>
                                    </div>
                                    <Text
                                        type="secondary"
                                        style={{ fontSize: 12 }}
                                    >
                                        С НДС − акт недостач
                                    </Text>
                                </Col>
                            </Row>

                            <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                                <Col xs={24} sm={12}>
                                    <Text type="secondary">
                                        Номер акта о нарушениях / недостач
                                    </Text>
                                    <Space
                                        wrap
                                        style={{ width: '100%', marginTop: 4 }}
                                    >
                                        <Input
                                            style={{ minWidth: 220, flex: 1 }}
                                            value={
                                                (
                                                    detail.steps || []
                                                ).find(
                                                    s =>
                                                        s.code ===
                                                        'penalty_act_number'
                                                )?.is_na
                                                    ? ''
                                                    : (detail.steps || []).find(
                                                          s =>
                                                              s.code ===
                                                              'penalty_act_number'
                                                      )?.text_value || ''
                                            }
                                            disabled={
                                                !!detail.archived_at ||
                                                !(detail.steps || []).some(
                                                    s =>
                                                        s.code ===
                                                        'penalty_act_number'
                                                ) ||
                                                Boolean(
                                                    (
                                                        detail.steps || []
                                                    ).find(
                                                        s =>
                                                            s.code ===
                                                            'penalty_act_number'
                                                    )?.is_na
                                                )
                                            }
                                            placeholder={
                                                (detail.steps || []).some(
                                                    s =>
                                                        s.code ===
                                                        'penalty_act_number'
                                                )
                                                    ? 'Номер акта'
                                                    : 'Нет в шаблоне'
                                            }
                                            onChange={e => {
                                                const val = e.target.value;
                                                setDetail(prev => {
                                                    if (!prev) return prev;
                                                    return {
                                                        ...prev,
                                                        steps: (
                                                            prev.steps || []
                                                        ).map(s =>
                                                            s.code ===
                                                            'penalty_act_number'
                                                                ? {
                                                                      ...s,
                                                                      text_value:
                                                                          val,
                                                                      is_na: false
                                                                  }
                                                                : s
                                                        )
                                                    };
                                                });
                                            }}
                                            onBlur={e => {
                                                const step = (
                                                    detail.steps || []
                                                ).find(
                                                    s =>
                                                        s.code ===
                                                        'penalty_act_number'
                                                );
                                                if (!step) return;
                                                saveStep(step, {
                                                    textValue: e.target.value,
                                                    isNa: false
                                                });
                                            }}
                                        />
                                        {(detail.steps || []).some(
                                            s =>
                                                s.code ===
                                                'penalty_act_number'
                                        ) ? (
                                            <Checkbox
                                                checked={Boolean(
                                                    (
                                                        detail.steps || []
                                                    ).find(
                                                        s =>
                                                            s.code ===
                                                            'penalty_act_number'
                                                    )?.is_na
                                                )}
                                                disabled={!!detail.archived_at}
                                                onChange={e => {
                                                    const step = (
                                                        detail.steps || []
                                                    ).find(
                                                        s =>
                                                            s.code ===
                                                            'penalty_act_number'
                                                    );
                                                    if (!step) return;
                                                    saveStep(step, {
                                                        isNa: e.target.checked,
                                                        textValue: ''
                                                    });
                                                }}
                                            >
                                                Не применимо
                                            </Checkbox>
                                        ) : null}
                                    </Space>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Text type="secondary">
                                        Сумма акта недостач
                                    </Text>
                                    <Space
                                        wrap
                                        style={{ width: '100%', marginTop: 4 }}
                                    >
                                        <InputNumber
                                            style={{ width: 220 }}
                                            value={
                                                (
                                                    detail.steps || []
                                                ).find(
                                                    s =>
                                                        s.code ===
                                                        'penalty_act_amount'
                                                )?.is_na
                                                    ? null
                                                    : (detail.steps || []).find(
                                                          s =>
                                                              s.code ===
                                                              'penalty_act_amount'
                                                      )?.money_value
                                            }
                                            disabled={
                                                !!detail.archived_at ||
                                                !(detail.steps || []).some(
                                                    s =>
                                                        s.code ===
                                                        'penalty_act_amount'
                                                ) ||
                                                Boolean(
                                                    (
                                                        detail.steps || []
                                                    ).find(
                                                        s =>
                                                            s.code ===
                                                            'penalty_act_amount'
                                                    )?.is_na
                                                )
                                            }
                                            min={0}
                                            step={0.01}
                                            onChange={v => {
                                                const step = (
                                                    detail.steps || []
                                                ).find(
                                                    s =>
                                                        s.code ===
                                                        'penalty_act_amount'
                                                );
                                                if (!step) return;
                                                setDetail(prev => {
                                                    if (!prev) return prev;
                                                    const penalty =
                                                        roundMoney(v) || 0;
                                                    const amountVat =
                                                        prev.amount_vat;
                                                    const amountToPay =
                                                        amountVat == null
                                                            ? null
                                                            : roundMoney(
                                                                  Math.max(
                                                                      0,
                                                                      amountVat -
                                                                          penalty
                                                                  )
                                                              );
                                                    return {
                                                        ...prev,
                                                        penalty_amount:
                                                            penalty,
                                                        amount_to_pay:
                                                            amountToPay,
                                                        balance:
                                                            amountToPay ==
                                                            null
                                                                ? null
                                                                : roundMoney(
                                                                      amountToPay -
                                                                          (prev.paid_amount ||
                                                                              0)
                                                                  ),
                                                        steps: (
                                                            prev.steps || []
                                                        ).map(s =>
                                                            s.code ===
                                                            'penalty_act_amount'
                                                                ? {
                                                                      ...s,
                                                                      money_value:
                                                                          v,
                                                                      is_na: false
                                                                  }
                                                                : s
                                                        )
                                                    };
                                                });
                                                saveStep(step, {
                                                    moneyValue: v,
                                                    isNa: false
                                                });
                                            }}
                                        />
                                        {(detail.steps || []).some(
                                            s =>
                                                s.code ===
                                                'penalty_act_amount'
                                        ) ? (
                                            <Checkbox
                                                checked={Boolean(
                                                    (
                                                        detail.steps || []
                                                    ).find(
                                                        s =>
                                                            s.code ===
                                                            'penalty_act_amount'
                                                    )?.is_na
                                                )}
                                                disabled={!!detail.archived_at}
                                                onChange={e => {
                                                    const step = (
                                                        detail.steps || []
                                                    ).find(
                                                        s =>
                                                            s.code ===
                                                            'penalty_act_amount'
                                                    );
                                                    if (!step) return;
                                                    saveStep(step, {
                                                        isNa: e.target.checked,
                                                        moneyValue: null
                                                    });
                                                }}
                                            >
                                                Не применимо
                                            </Checkbox>
                                        ) : null}
                                    </Space>
                                </Col>
                            </Row>

                            <Row gutter={[12, 8]} style={{ marginTop: 12 }}>
                                <Col span={8}>
                                    Оплачено: {money(detail.paid_amount)}
                                </Col>
                                <Col span={8}>
                                    Остаток: {money(detail.balance)}
                                </Col>
                            </Row>
                            {!detail.archived_at ? (
                                <Button
                                    type="primary"
                                    ghost
                                    style={{ marginTop: 12 }}
                                    onClick={() =>
                                        saveAmounts({
                                            amountNoVat: detail.amount_no_vat,
                                            amountVat: detail.amount_vat
                                        })
                                    }
                                >
                                    Сохранить суммы
                                </Button>
                            ) : null}
                            {(detail.payments || []).length ? (
                                <Table
                                    style={{ marginTop: 12 }}
                                    size="small"
                                    pagination={false}
                                    rowKey="id"
                                    dataSource={detail.payments}
                                    columns={[
                                        {
                                            title: 'Дата',
                                            dataIndex: 'paid_at',
                                            width: 120
                                        },
                                        {
                                            title: 'Сумма',
                                            dataIndex: 'amount',
                                            render: v => money(v)
                                        },
                                        {
                                            title: 'Комментарий',
                                            dataIndex: 'comment',
                                            ellipsis: true
                                        },
                                        {
                                            title: '',
                                            width: 64,
                                            render: (_, row) =>
                                                detail.archived_at ? null : (
                                                    <Popconfirm
                                                        title="Удалить оплату?"
                                                        okText="Удалить"
                                                        cancelText="Отмена"
                                                        onConfirm={() =>
                                                            deletePayment(
                                                                row.id
                                                            )
                                                        }
                                                    >
                                                        <Button
                                                            type="text"
                                                            danger
                                                            icon={
                                                                <DeleteOutlined />
                                                            }
                                                        />
                                                    </Popconfirm>
                                                )
                                        }
                                    ]}
                                />
                            ) : null}
                            {!detail.archived_at ? (
                                <Form
                                    form={paymentForm}
                                    layout="inline"
                                    style={{ marginTop: 12 }}
                                    onFinish={addPayment}
                                >
                                    <Form.Item
                                        name="amount"
                                        rules={[{ required: true }]}
                                    >
                                        <InputNumber
                                            placeholder="Сумма оплаты"
                                            min={0}
                                        />
                                    </Form.Item>
                                    <Form.Item name="paidAt">
                                        <DatePicker format="DD.MM.YYYY" />
                                    </Form.Item>
                                    <Form.Item name="comment">
                                        <Input placeholder="Комментарий" />
                                    </Form.Item>
                                    <Button type="primary" htmlType="submit">
                                        Добавить оплату
                                    </Button>
                                </Form>
                            ) : null}
                        </Card>

                        <Card size="small" title="Дорожная карта по разделам">
                            <Collapse
                                accordion={false}
                                defaultActiveKey={groupSteps(detail.steps)
                                    .filter(sec =>
                                        sec.steps.some(s =>
                                            [
                                                'waiting',
                                                'due_soon',
                                                'overdue',
                                                'empty',
                                                'waiting_t2'
                                            ].includes(s.status)
                                        )
                                    )
                                    .map(sec => sec.key)
                                    .slice(0, 2)}
                                items={groupSteps(detail.steps).map(sec => ({
                                    key: sec.key,
                                    label: (
                                        <Space>
                                            <Text strong>{sec.title}</Text>
                                            <Tag>
                                                {sectionProgress(sec.steps)}%
                                            </Tag>
                                            <Text type="secondary">
                                                {
                                                    sec.steps.filter(s =>
                                                        [
                                                            'done_on_time',
                                                            'done_late',
                                                            'done',
                                                            'na'
                                                        ].includes(s.status)
                                                    ).length
                                                }
                                                /{sec.steps.length}
                                            </Text>
                                        </Space>
                                    ),
                                    children: (
                                        <Space
                                            direction="vertical"
                                            size={8}
                                            style={{ width: '100%' }}
                                        >
                                            {sec.steps.map(step => (
                                                <Card
                                                    key={step.id}
                                                    size="small"
                                                    style={{
                                                        borderColor:
                                                            step.status ===
                                                            'overdue'
                                                                ? '#ff4d4f'
                                                                : step.status ===
                                                                    'due_soon'
                                                                  ? '#faad14'
                                                                  : undefined
                                                    }}
                                                    title={
                                                        <Space wrap>
                                                            <span>
                                                                {step.title}
                                                            </span>
                                                            {statusTag(
                                                                step.status
                                                            )}
                                                        </Space>
                                                    }
                                                    extra={
                                                        <Text type="secondary">
                                                            SLA{' '}
                                                            {step.sla_days ??
                                                                '—'}{' '}
                                                            дн.
                                                            {step.due_at
                                                                ? ` · до ${step.due_at}`
                                                                : ''}
                                                            {step.days_overdue
                                                                ? ` · +${step.days_overdue} дн.`
                                                                : ''}
                                                        </Text>
                                                    }
                                                >
                                                    <Space
                                                        direction="vertical"
                                                        size={8}
                                                        style={{
                                                            width: '100%'
                                                        }}
                                                    >
                                                        <Space wrap>
                                                            <span>
                                                                Ответственный:
                                                            </span>
                                                            <Select
                                                                allowClear
                                                                showSearch
                                                                optionFilterProp="label"
                                                                style={{
                                                                    minWidth: 240
                                                                }}
                                                                disabled={
                                                                    !!detail.archived_at ||
                                                                    savingStep ===
                                                                        step.id
                                                                }
                                                                value={
                                                                    step.responsible_user_id ||
                                                                    undefined
                                                                }
                                                                placeholder={
                                                                    step.responsible ||
                                                                    'Назначить'
                                                                }
                                                                options={
                                                                    userOptions
                                                                }
                                                                onChange={v =>
                                                                    saveStep(
                                                                        step,
                                                                        {
                                                                            responsibleUserId:
                                                                                v ||
                                                                                null
                                                                        }
                                                                    )
                                                                }
                                                            />
                                                        </Space>

                                                        <Space wrap>
                                                            <span>
                                                                Срок (дн.):
                                                            </span>
                                                            <InputNumber
                                                                disabled={
                                                                    !!detail.archived_at ||
                                                                    savingStep ===
                                                                        step.id
                                                                }
                                                                value={
                                                                    step.sla_days
                                                                }
                                                                onChange={v =>
                                                                    saveStep(
                                                                        step,
                                                                        {
                                                                            slaDaysOverride:
                                                                                v
                                                                        }
                                                                    )
                                                                }
                                                            />
                                                            <span>Дедлайн:</span>
                                                            <DatePicker
                                                                format="DD.MM.YYYY"
                                                                disabled={
                                                                    !!detail.archived_at ||
                                                                    savingStep ===
                                                                        step.id
                                                                }
                                                                value={
                                                                    step.due_at_override ||
                                                                    step.due_at
                                                                        ? dayjs(
                                                                              step.due_at_override ||
                                                                                  step.due_at
                                                                          )
                                                                        : null
                                                                }
                                                                onChange={v =>
                                                                    saveStep(
                                                                        step,
                                                                        {
                                                                            dueAtOverride:
                                                                                v
                                                                                    ? v.format(
                                                                                          'YYYY-MM-DD'
                                                                                      )
                                                                                    : null
                                                                        }
                                                                    )
                                                                }
                                                            />
                                                        </Space>

                                                        {step.kind ===
                                                        'date' ? (
                                                            <Space wrap>
                                                                <DatePicker
                                                                    value={
                                                                        step.done_at
                                                                            ? dayjs(
                                                                                  step.done_at
                                                                              )
                                                                            : null
                                                                    }
                                                                    format="DD.MM.YYYY"
                                                                    allowClear
                                                                    disabled={
                                                                        step.is_na ||
                                                                        savingStep ===
                                                                            step.id ||
                                                                        !!detail.archived_at
                                                                    }
                                                                    onChange={value =>
                                                                        saveStep(
                                                                            step,
                                                                            {
                                                                                doneAt:
                                                                                    value
                                                                                        ? value.format(
                                                                                              'YYYY-MM-DD'
                                                                                          )
                                                                                        : null,
                                                                                isNa: false
                                                                            }
                                                                        )
                                                                    }
                                                                />
                                                                <Checkbox
                                                                    checked={
                                                                        step.is_na
                                                                    }
                                                                    disabled={
                                                                        savingStep ===
                                                                            step.id ||
                                                                        !!detail.archived_at
                                                                    }
                                                                    onChange={e =>
                                                                        saveStep(
                                                                            step,
                                                                            {
                                                                                isNa: e
                                                                                    .target
                                                                                    .checked,
                                                                                doneAt: null
                                                                            }
                                                                        )
                                                                    }
                                                                >
                                                                    Не
                                                                    применимо
                                                                </Checkbox>
                                                            </Space>
                                                        ) : null}

                                                        {step.kind ===
                                                        'text' ? (
                                                            <Space wrap>
                                                                <Input
                                                                    key={`text-${step.id}-${step.is_na}-${step.text_value || ''}`}
                                                                    style={{
                                                                        minWidth: 220
                                                                    }}
                                                                    defaultValue={
                                                                        step.is_na
                                                                            ? ''
                                                                            : step.text_value
                                                                    }
                                                                    disabled={
                                                                        step.is_na ||
                                                                        savingStep ===
                                                                            step.id ||
                                                                        !!detail.archived_at
                                                                    }
                                                                    onBlur={e =>
                                                                        saveStep(
                                                                            step,
                                                                            {
                                                                                textValue:
                                                                                    e
                                                                                        .target
                                                                                        .value,
                                                                                isNa: false
                                                                            }
                                                                        )
                                                                    }
                                                                />
                                                                <Checkbox
                                                                    checked={
                                                                        step.is_na
                                                                    }
                                                                    disabled={
                                                                        savingStep ===
                                                                            step.id ||
                                                                        !!detail.archived_at
                                                                    }
                                                                    onChange={e =>
                                                                        saveStep(
                                                                            step,
                                                                            {
                                                                                isNa: e
                                                                                    .target
                                                                                    .checked,
                                                                                textValue:
                                                                                    ''
                                                                            }
                                                                        )
                                                                    }
                                                                >
                                                                    Не
                                                                    применимо
                                                                </Checkbox>
                                                            </Space>
                                                        ) : null}

                                                        {step.kind ===
                                                        'money' ? (
                                                            <Space wrap>
                                                                <InputNumber
                                                                    style={{
                                                                        width: 220
                                                                    }}
                                                                    value={
                                                                        step.is_na
                                                                            ? null
                                                                            : step.money_value
                                                                    }
                                                                    disabled={
                                                                        step.is_na ||
                                                                        savingStep ===
                                                                            step.id ||
                                                                        !!detail.archived_at
                                                                    }
                                                                    onChange={v =>
                                                                        saveStep(
                                                                            step,
                                                                            {
                                                                                moneyValue:
                                                                                    v,
                                                                                isNa: false
                                                                            }
                                                                        )
                                                                    }
                                                                />
                                                                <Checkbox
                                                                    checked={
                                                                        step.is_na
                                                                    }
                                                                    disabled={
                                                                        savingStep ===
                                                                            step.id ||
                                                                        !!detail.archived_at
                                                                    }
                                                                    onChange={e =>
                                                                        saveStep(
                                                                            step,
                                                                            {
                                                                                isNa: e
                                                                                    .target
                                                                                    .checked,
                                                                                moneyValue:
                                                                                    null
                                                                            }
                                                                        )
                                                                    }
                                                                >
                                                                    Не
                                                                    применимо
                                                                </Checkbox>
                                                            </Space>
                                                        ) : null}

                                                        <TextArea
                                                            rows={2}
                                                            placeholder="Комментарий по этапу"
                                                            defaultValue={
                                                                step.comment
                                                            }
                                                            disabled={
                                                                savingStep ===
                                                                    step.id ||
                                                                !!detail.archived_at
                                                            }
                                                            onBlur={e => {
                                                                if (
                                                                    e.target
                                                                        .value !==
                                                                    (step.comment ||
                                                                        '')
                                                                ) {
                                                                    saveStep(
                                                                        step,
                                                                        {
                                                                            comment:
                                                                                e
                                                                                    .target
                                                                                    .value
                                                                        }
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    </Space>
                                                </Card>
                                            ))}
                                        </Space>
                                    )
                                }))}
                            />
                        </Card>

                        {(detail.audits || []).length ? (
                            <Card size="small" title="История изменений">
                                <Timeline
                                    items={detail.audits.map(a => ({
                                        children: (
                                            <div>
                                                <Text strong>{a.field}</Text>
                                                <Text type="secondary">
                                                    {' '}
                                                    {a.user_name || 'система'} ·{' '}
                                                    {a.created_at
                                                        ? dayjs(
                                                              a.created_at
                                                          ).format(
                                                              'DD.MM.YYYY HH:mm'
                                                          )
                                                        : ''}
                                                </Text>
                                                <div>
                                                    <Text type="secondary">
                                                        {a.old_value || '—'} →{' '}
                                                        {a.new_value || '—'}
                                                    </Text>
                                                </div>
                                            </div>
                                        )
                                    }))}
                                />
                            </Card>
                        ) : null}
                    </Space>
                ) : null}
            </Drawer>
        </Layout>
    );
};

export default BpClosingPage;
